import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Calculator as CalcIcon, Loader2 } from "lucide-react";
import InvestorShell from "./InvestorShell";
import { api, money } from "./api";

const empty = { vehicle_id: "", hourly_rate: "", weekly_hours: "", deductions: 0, vehicle_expenses: 0, maintenance_reserve: 0, other_reserves: 0, tier_pct: 15 };

export default function Calculator() {
  const [vehicles, setVehicles] = useState([]);
  const [gates, setGates] = useState(null);
  const [form, setForm] = useState(empty);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/investor/vehicles").then(({ data }) => setVehicles(data)).catch((e) => toast.error(e?.response?.data?.detail || "Sign in with an approved investor profile to use the calculator"));
    api.get("/investor/me").then(({ data }) => setGates(data.gates)).catch(() => {});
  }, []);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setResult(null); };
  const valid = form.vehicle_id && Number(form.hourly_rate) > 0 && Number(form.weekly_hours) >= 0 && form.weekly_hours !== "";

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/investor/calculator", {
        ...form,
        hourly_rate: Number(form.hourly_rate), weekly_hours: Number(form.weekly_hours),
        deductions: Number(form.deductions) || 0, vehicle_expenses: Number(form.vehicle_expenses) || 0,
        maintenance_reserve: Number(form.maintenance_reserve) || 0, other_reserves: Number(form.other_reserves) || 0,
        tier_pct: Number(form.tier_pct),
      });
      setResult(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Calculation failed"); }
    finally { setBusy(false); }
  };

  const Num = ({ k, label, step = "1" }) => (
    <div>
      <label className="field-label" htmlFor={`calc-${k}`}>{label}</label>
      <input id={`calc-${k}`} type="number" min="0" step={step} className="field" value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`calc-${k}`} />
    </div>
  );

  return (
    <InvestorShell gates={gates} title="Illustrative Revenue Calculator" subtitle="Enter your own assumptions — nothing here is a forecast or guarantee">
      <div className="grid gap-6 lg:grid-cols-2" data-testid="calculator-page">
        <div className="card-line rounded-xl bg-white p-6">
          <h2 className="flex items-center gap-2 font-display text-base font-extrabold text-offwhite"><CalcIcon size={15} className="text-red-700" /> Your assumptions</h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div>
              <label className="field-label" htmlFor="calc-vehicle">Vehicle</label>
              <select id="calc-vehicle" className="field" value={form.vehicle_id} onChange={(e) => set("vehicle_id", e.target.value)} data-testid="calc-vehicle">
                <option value="">Select vehicle…</option>
                {vehicles.map((v) => <option key={v.vehicle_id} value={v.vehicle_id}>{v.name} ({money(v.acquisition_cost)})</option>)}
              </select>
            </div>
            <div>
              <label className="field-label" htmlFor="calc-tier">Proposed tier</label>
              <select id="calc-tier" className="field" value={form.tier_pct} onChange={(e) => set("tier_pct", e.target.value)} data-testid="calc-tier">
                {[15, 25, 100].map((t) => <option key={t} value={t}>{t}% funding tier</option>)}
              </select>
            </div>
            <Num k="hourly_rate" label="Hourly rental rate ($)" />
            <Num k="weekly_hours" label="Eligible completed weekly rental hours" />
            <Num k="deductions" label="Deductions / refunds ($/week)" />
            <Num k="vehicle_expenses" label="Defined vehicle expenses ($/week)" />
            <Num k="maintenance_reserve" label="Maintenance reserve ($/week)" />
            <Num k="other_reserves" label="Other reserves ($/week)" />
          </div>
          <p className="mt-4 rounded bg-surface/70 p-3 text-[11px] leading-relaxed text-muted">
            Fleet partner share follows the weekly tier rule: 1–15 hours → partner 40%; 16+ hours → partner 60% on all eligible hours that week.
          </p>
          <button onClick={run} disabled={!valid || busy} className="mt-4 w-full rounded-full bg-red-700 px-7 py-3.5 font-display text-sm font-bold uppercase tracking-[0.12em] text-white hover:bg-red-600 disabled:opacity-50" data-testid="calc-run-btn">
            {busy ? <Loader2 size={15} className="mx-auto animate-spin" /> : "Calculate (Illustrative Only)"}
          </button>
        </div>

        <div className="card-line rounded-xl bg-white p-6" data-testid="calc-results">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-base font-extrabold text-offwhite">Results</h2>
            {result && <span className="rounded-full border border-yellow-600/40 bg-yellow-400/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-yellow-700" data-testid="calc-label">{result.label}</span>}
          </div>
          {!result && <p className="mt-6 text-sm text-muted">Results stay at zero until you enter assumptions and calculate.</p>}
          {result && (
            <>
              <dl className="mt-5 space-y-2.5 text-sm">
                {[
                  ["Proposed contribution", money(result.contribution)],
                  ["Gross rental revenue (weekly)", money(result.gross_rental_revenue)],
                  ["Net rental revenue", money(result.net_rental_revenue)],
                  [`Fleet partner share (${result.partner_pct_applied}%)`, money(result.fleet_partner_share)],
                  ["Xtreme Exotics share", money(result.xtreme_exotics_share)],
                  ["Defined Vehicle Profit", money(result.defined_vehicle_profit)],
                  [`Illustrative investor distribution (${result.tier_pct === 100 ? 50 : result.tier_pct}% of DVP)`, money(result.illustrative_investor_distribution)],
                  ["Xtreme Exotics retained", money(result.xtreme_exotics_retained)],
                  ["Illustrative cash-on-cash (annualized)", `${result.cash_on_cash_pct}%`],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-3 border-b border-black/[0.05] pb-2">
                    <dt className="text-muted">{k}</dt><dd className="font-mono font-bold text-offwhite">{v}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-4 rounded-lg border border-red-500/25 bg-red-500/[0.06] p-3 text-[11px] leading-relaxed text-red-800" data-testid="calc-disclaimer">{result.disclaimer}</p>
            </>
          )}
        </div>
      </div>
    </InvestorShell>
  );
}
